import React, { useEffect, useState } from "react";
import Button from "Components/common/Button";
import { InputTextBox } from "Components/common/InputTextBox";
import { makeApi, URLS } from "Utils/apiURL";
import { toasterState } from "Components/common/Toaster";

const PhoneNumberChargePopup = (props) => {
  const { teamId = "", close = () => {} } = props || {};

  const [numbers, setNumbers] = useState([]);
  const [charges, setCharges] = useState({});
  const [originalCharges, setOriginalCharges] = useState({});
  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchPhoneNumbers = async () => {
    if (!teamId) return;
    setFetching(true);
    try {
      const response = await makeApi({
        url: `${URLS.getDialerPhoneNumbers}?teamId=${teamId}`,
        method: "GET",
      });
      const data = response?.data || [];
      setNumbers(data);
      const initialCharges = {};
      data.forEach((n) => {
        initialCharges[n.phoneNumber] = n.chargedAmount;
      });
      setCharges(initialCharges);
      setOriginalCharges(initialCharges);
    } catch (err) {
      toasterState.setToastMsg("Failed to fetch phone numbers", "fail");
    }
    setFetching(false);
  };

  useEffect(() => {
    fetchPhoneNumbers();
  }, [teamId]);

  const handleChargeChange = (id, value) => {
    setCharges((prev) => ({ ...prev, [id]: value }));
  };

  const saveAll = async () => {
    const changed = numbers.filter(
      (n) => parseFloat(charges[n.phoneNumber]) !== parseFloat(originalCharges[n.phoneNumber])
    );
    if (changed.length === 0) {
      toasterState.setToastMsg("No changes to save", "fail");
      return;
    }
    setSaving(true);
    let allSuccess = true;
    for (const number of changed) {
      const newCharge = parseFloat(charges[number.phoneNumber]);
      if (isNaN(newCharge) || newCharge < 0) {
        toasterState.setToastMsg(
          `Invalid charge for ${number.phoneNumber}`,
          "fail"
        );
        allSuccess = false;
        continue;
      }
      try {
        const response = await makeApi({
          url: URLS.updatePhoneNumberCharge,
          method: "POST",
          data: { phoneNumber: number.phoneNumber, monthlyCharge: newCharge },
        });
        if (response?.data?.status !== "updated") {
          allSuccess = false;
        }
      } catch (err) {
        allSuccess = false;
      }
    }
    setSaving(false);
    if (allSuccess) {
      toasterState.setToastMsg("Charges updated successfully", "success");
      close();
    } else {
      toasterState.setToastMsg("Some charges failed to update", "fail");
    }
  };

  const saveButton = {
    id: "savePhoneChargesBtn",
    name: "savePhoneChargesBtn",
    type: "button",
    className: `primaryButton ${saving ? "loading" : ""}`,
    btnText: "Save Changes",
    loader: saving,
    click: saveAll,
  };

  return (
    <div className="popup">
      <div className="popupBody phoneNumberChargePopup">
        <i className="material-icons close" onClick={close}>
          close
        </i>
        <div className="popupCard">
          <h3>Update Phone Number Monthly Charges</h3>
          {fetching ? (
            <div className="phoneChargeLoader">
              <span className="loadingText">Fetching phone numbers...</span>
            </div>
          ) : numbers.length === 0 ? (
            <div className="noNumbersMsg">
              <i className="material-icons">phone_disabled</i>
              <span>No active phone numbers found for this team.</span>
            </div>
          ) : (
            <div className="phoneNumberList">
              <div className="phoneNumberListHeader">
                <span className="colNumber">Phone Number</span>
                <span className="colType">Type</span>
                <span className="colCharge">Monthly Charge ($)</span>
              </div>
              {numbers.map((n) => (
                <div key={n.phoneNumber} className="phoneNumberRow">
                  <span className="colNumber">{n.phoneNumber}</span>
                  <span className={`colType typeBadge ${n.type.toLowerCase()}`}>
                    {n.type}
                  </span>
                  <div className="colCharge">
                    <InputTextBox
                      id={n.phoneNumber}
                      name={n.phoneNumber}
                      type="number"
                      value={charges[n.phoneNumber] ?? n.chargedAmount}
                      allowFloatNumber={true}
                      minValue={0}
                      change={handleChargeChange}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="popupFooter">
          <Button {...saveButton} />
        </div>
      </div>
      <div className="bgOverlay" onClick={() => !saving && close()} />
    </div>
  );
};

export default PhoneNumberChargePopup;
